import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/router";
import Link from "next/link";

import { Button, Card, Chip, Textarea } from "@chef/components";
import { AppShell } from "@chef/feature-app-shell";
import { ChevronLeft, Close } from "@chef/icons/small";
import {
  showNotification,
  useUpdateBillingAgreementMutation,
} from "@chef/state-management";
import { useIntercom } from "@chef/hooks";
import { ACCOUNT_STATUSES, BRAND_PATHS } from "@chef/constants";
import { useTrack } from "@chef/state-management/hooks";

import { useTrackingData } from "../../../hooks/useTrackingData";
import { steps } from "./CancelSubscriptionSteps";

import { intl } from "./CancelSubscriptionPage.Intl";

export type Level =
  | "main_freeze_reason"
  | "sub_freeze_reason"
  | "sub_freeze_followup_answer"
  | "sub_freeze_followup_question";

interface SURVEY_STEP {
  title: string;
  level?: Level;
  options: {
    text: string;
    next?: SURVEY_STEP;
  }[];
}

interface HandleSurveyStepProps extends SURVEY_STEP {
  selected: string;
  onSelect: (value: string) => void;
}

const HandleSurveyStep = ({
  title,
  options,
  selected,
  onSelect,
}: HandleSurveyStepProps) => {
  return (
    <div>
      <div className="flex flex-col">
        <h2 className="mt-8 text-2xl">
          <strong>{title}</strong>
        </h2>
        <p className="mt-2 text-sm">{intl.CHOOSE_ONE_OF_THE_OPTIONS_BELOW}</p>
      </div>

      <ul className="flex flex-col flex-wrap justify-center gap-3 mt-8 gap-y-5">
        {options.map((option, indx) => {
          const { text } = option;

          return (
            <li key={indx}>
              <Chip onClick={() => onSelect(text)} selected={selected === text}>
                {text}
              </Chip>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export const CancelSubscriptionPage = () => {
  useIntercom(false);
  const router = useRouter();

  const track = useTrack();
  useEffect(() => {
    track("updateSubscriptionStepViewed", {
      affiliation: "Frontend process",
      change_subscription_details: "Cancel subscription",
      url: window.location.href,
    });
  }, [track]);

  const [updateBillingAgreementMutation, { isLoading }] =
    useUpdateBillingAgreementMutation();

  const [surveyStep, setSurveyStep] = useState<SURVEY_STEP | undefined>(
    steps.START_SURVEY_STEP,
  );

  const previousSurveySteps = useRef<SURVEY_STEP[]>([]);

  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [selectedSubFreezeQuestion, setSelectedSubFreezeQuestion] =
    useState("");

  const [answers, setAnswers] = useState<{
    main_freeze_reason: string;
    sub_freeze_reason: string;
    sub_freeze_followup_answer: string;
  }>({
    main_freeze_reason: "",
    sub_freeze_reason: "",
    sub_freeze_followup_answer: "",
  });

  const [whatCanWeDoBetterText, setWhatCanWeDoBetterText] = useState("");

  const trackingData = useTrackingData();

  const handleGoNext = async () => {
    if (!surveyStep) {
      try {
        await updateBillingAgreementMutation({
          status: ACCOUNT_STATUSES.PAUSED,
        }).unwrap();

        track("updateSubscriptionStepCompleted", {
          affiliation: "Frontend process",
          changed_subscription_details: "Cancel subscription",
          url: window.location.href,
        });
        track("freezedSubscriptionSurveyCompleted", {
          affiliation: "Frontend process",
          freeze_reason_comment: whatCanWeDoBetterText,
          sub_freeze_followup_question: selectedSubFreezeQuestion,
          ...answers,
        });
        track("subscriptionUpdated", {
          affiliation: "Frontend process",
          payment_method: trackingData?.payment_method || "cc",
          products: trackingData?.products,
          status: ACCOUNT_STATUSES.PAUSED,
          timeblock_day: trackingData?.timeblock_day,
          timeblock_id: trackingData?.timeblock_id,
          timeblock_interval: trackingData?.timeblock_interval,
        });

        router.push(BRAND_PATHS.PAUSE_SUBSCRIPTION_SUCCESS_PAGE);
      } catch (error) {
        showNotification({
          type: "error",
          message: intl.CAN_NOT_CHANGE_SUBSCRIPTION_STATUS,
        });
        router.push(BRAND_PATHS.MY_SUBSCRIPTION_LINK);
      }
    }

    if (surveyStep) {
      const subQuestion = surveyStep.options.find(
        (opt) => opt.text === selectedAnswer,
      )?.next;

      setSelectedAnswer("");
      setSurveyStep(subQuestion);

      if (subQuestion?.level === "sub_freeze_followup_answer") {
        setSelectedSubFreezeQuestion(subQuestion?.title);
      }

      previousSurveySteps.current.push(surveyStep);

      return;
    }
  };

  const handleGoToPreviousStep = async () => {
    if (surveyStep?.title === steps.START_SURVEY_STEP.title) {
      router.push(BRAND_PATHS.CHOOSE_OPTION_TO_PAUSE);
    } else {
      setSurveyStep(previousSurveySteps.current.pop());
      return;
    }
  };

  const handleSelectAnswer = (value: string) => {
    setSelectedAnswer(value);
    if (surveyStep?.level === "main_freeze_reason") {
      setAnswers({
        [surveyStep.level]: value,
        sub_freeze_reason: "",
        sub_freeze_followup_answer: "",
      });
    }

    setAnswers((prev) => ({ ...prev, [surveyStep?.level as string]: value }));
  };

  return (
    <AppShell.Main
      className="flex-grow bg-gradient-to-b from-background to-grey-2 md:py-32"
      grid
    >
      <div className="h-full col-span-full md:col-start-2 xl:col-start-4 md:col-span-6">
        <Card noPadding className="flex flex-col justify-between">
          <div className="p-8 text-center">
            <div className="relative flex items-center justify-between w-full text-sm">
              <button onClick={handleGoToPreviousStep}>
                <ChevronLeft />
              </button>
              <div>{intl.PAUSE_SUBSCRIPTION}</div>
              <Link href={BRAND_PATHS.MY_SUBSCRIPTION_LINK} passHref>
                <Close />
              </Link>
            </div>

            <div className="md:px-10">
              {surveyStep ? (
                <HandleSurveyStep
                  options={surveyStep?.options || []}
                  title={surveyStep?.title || ""}
                  onSelect={(value) => handleSelectAnswer(value)}
                  selected={selectedAnswer}
                />
              ) : (
                <>
                  <div className="flex flex-col">
                    <h1 className="mt-8 text-2xl">
                      <strong>{intl.SUGGESTIONS_TO_IMPROVE}</strong>
                    </h1>
                    <p className="mt-4">
                      {intl.SUGGESTIONS_TO_IMPROVE_DESCRIPTION}
                    </p>
                  </div>
                  <Textarea
                    value={whatCanWeDoBetterText}
                    onChange={(e) => setWhatCanWeDoBetterText(e.target.value)}
                    maxLength={256}
                    className="mt-6"
                    rows={6}
                    helperText={intl.OPTIONAL}
                    placeholder={intl.ADD_COMMENT}
                  />
                </>
              )}
            </div>
          </div>

          <footer className="pt-20">
            <Card
              noPadding
              className="flex justify-between w-full py-3 md:justify-end"
            >
              <Link
                href={BRAND_PATHS.MY_SUBSCRIPTION_LINK}
                passHref
                legacyBehavior
              >
                <Button className="ml-2 px-11" outlined>
                  {intl.CANCEL}
                </Button>
              </Link>

              <Button
                primary
                onClick={handleGoNext}
                loading={isLoading}
                disabled={surveyStep && !selectedAnswer}
                className="mr-2 px-11 md:ml-4"
              >
                {intl.GO_FORWARD}
              </Button>
            </Card>
          </footer>
        </Card>
      </div>
    </AppShell.Main>
  );
};
